import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button } from '@avangard/ui/core';
import { Add } from '@material-ui/icons';

import { FormStyled } from '@modules/layout/styled';
import { useStudent } from '@modules/students/hooks';
import { LapAccessCard } from '@modules/lap/moleculas';
import { LapAccessCreateFormModal } from '@modules/lap/organisms';
import { StudentLapAccessForm } from '@modules/students/organisms';
import { LapAccessTarget } from '@modules/types/graphql';

import type { StudentEntity } from '@modules/students/entities';

type StudentPlatformsAccessProps = {
    canCreate: boolean;
    canDelete: boolean;
    student: StudentEntity;
};

const Root = styled(FormStyled.FormColumn)`
    max-width: 100%;
`;

const FormRowCard = styled(FormStyled.FormRow)`
    display: grid;
    grid-template-columns: repeat(auto-fit, 256px);
    grid-gap: 16px;
`;

const addIcon = <Add style={{ fontSize: 16 }} />;

const StudentPlatformsAccess = (props: StudentPlatformsAccessProps): React.ReactElement => {
    const { canCreate, canDelete, student } = props;

    const { t } = useTranslation(['common', 'access']);

    const laps = student.getUniqueLapAccessList();

    const { refetch: refetchStudent } = useStudent({ skip: true });

    const [openLapAccessCreateModal, setOpenLapAccessCreateModal] = React.useState<boolean>(false);

    const handleOpenLapAccessCreateModal = (): void => setOpenLapAccessCreateModal(true);
    const handleCloseLapAccessCreateModal = (): void => setOpenLapAccessCreateModal(false);

    const handleRefetchStudent = async (): Promise<void> => {
        await refetchStudent({ id: student.id });
    };

    const singleAccess = student.lapAccessList.length === 1;

    return (
        <Root>
            <FormStyled.FormColumnTitle>{t('access:titles.platforms')}</FormStyled.FormColumnTitle>

            <FormStyled.FormRow>
                <Button
                    startIcon={addIcon}
                    onClick={handleOpenLapAccessCreateModal}
                    disabled={!canCreate}
                >
                    {t('access:actions.add_access')}
                </Button>
            </FormStyled.FormRow>

            <FormRowCard>
                {student.lapAccessList.map(lapAccess =>
                    lapAccess ? (
                        <LapAccessCard
                            disableEdit
                            key={lapAccess.id}
                            lapAccess={lapAccess}
                            disableRemove={!canDelete || singleAccess}
                            onRemoveLapAccess={handleRefetchStudent}
                        />
                    ) : null,
                )}
            </FormRowCard>

            {canCreate ? (
                <LapAccessCreateFormModal
                    target={LapAccessTarget.student}
                    targetId={student.id}
                    onCreateLapAccess={handleRefetchStudent}
                    open={openLapAccessCreateModal}
                    onClose={handleCloseLapAccessCreateModal}
                >
                    <StudentLapAccessForm laps={laps} onCancel={handleCloseLapAccessCreateModal} />
                </LapAccessCreateFormModal>
            ) : null}
        </Root>
    );
};

export { StudentPlatformsAccess };
